import { NgClass, NgStyle } from '@angular/common';
import {
    Component,
    computed,
    ContentChildren,
    ElementRef,
    Host,
    input,
    model,
    OnChanges,
    OnInit,
    Optional,
    output,
    QueryList,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { Button } from '../button/button.component';
import { Icon } from '../icon/icon.component';
import { MenuManager } from './menu.service';
import '../../utils/array-extensions';
import { Router } from '@angular/router';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';

/**
 * Representa um item de um Menu.
 *
 */
export type MenuItem = {
    id?: string | null | undefined;
    label: string;
    /**
     * Texto secundário, exibido em fonte menor e cor discreta logo abaixo do
     * `label` (ex.: um detalhe complementar sobre o item, como a
     * especialidade de um modelo de IA). Omitido quando `null`/`undefined`.
     */
    description?: string | null | undefined;
    fontIcon?: string | null | undefined;
    imageIcon?: string | null | undefined;
    postLabelIcons?: { source: string; tooltip: string }[] | null | undefined;
    url?: string | null | undefined;
    routerLink?: string[] | null | undefined;
    data?: any;
    children?: MenuItem[];
    childMenu?: Menu | null | undefined;
    disabled?: boolean | null | undefined;
    /** Realça o item como ação destrutiva (texto/ícone em vermelho). */
    danger?: boolean | null | undefined;
};

@Component({
    selector: 'ai-menu-header',
    standalone: true,
    template: ` <ng-content></ng-content>`,
    imports: [],
})
export class MenuHeader {}

/**
 * Representa uma opção ou item do menu.
 */
@Component({
    selector: 'ai-menu-option',
    standalone: true,
    template: '',
})
export class MenuOption {
    readonly label = input<string>('');
    readonly fontIcon = input<string | null | undefined>(null);
    readonly imageIcon = input<string | null | undefined>(null);
    readonly postLabelIcons = input<
        { source: string; tooltip: string }[] | null | undefined
    >(null);
    readonly url = input<string | null | undefined>(null);
    readonly routerLink = input<string[] | null | undefined>(null);
    readonly data = input<any | null>(null);
    children?: MenuOption[];
    childMenu?: Menu | null | undefined;

    public getMenuItem(): MenuItem {
        return {
            id: this.label(),
            label: this.label(),
            fontIcon: this.fontIcon(),
            imageIcon: this.imageIcon(),
            postLabelIcons: this.postLabelIcons(),
            url: this.url(),
            data: this.data(),
            routerLink: this.routerLink(),
            children: this.children?.map((child) => child.getMenuItem()) || [],
            childMenu: this.childMenu,
        };
    }
}

/**
 * Menu suspenso com itens, suporte a submenus e cabeçalho opcional.
 *
 * Pode ser usado de forma autônoma ou como menu de um {@link Button} (detectado
 * via host). Itens podem navegar por rota (`routerLink`) ou apenas emitir
 * {@link itemClick}. Registra-se no {@link MenuManager}.
 *
 * Usa `ViewEncapsulation.None` (estilos `.ai-menu*` compartilhados).
 */
@Component({
    selector: 'ai-menu',
    standalone: true,
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [NgStyle, NgClass, Icon, TooltipDirective],
})
export class Menu implements OnChanges, OnInit {
    /** Identificador do menu (atribuído pelo MenuManager). */
    id: string | null | undefined;

    /** Emite o item clicado (quando não é submenu nem navegação). */
    readonly itemClick = output<MenuItem>();

    /** Define se o menu flutua sobre o conteúdo. */
    readonly floating = input<boolean>(true);

    /** Itens exibidos no menu. */
    readonly items = input<MenuItem[]>();

    /** Classe CSS extra aplicada ao menu. */
    readonly styleClass = input<string>();

    /** Estilo inline do menu (largura/posição/visibilidade). */
    readonly style = model<any>({ width: '300px' });

    /** Indica se há um cabeçalho (`ai-menu-header`) projetado. */
    readonly hasHeader = computed(() => this.header.length > 0);

    @ContentChildren(MenuHeader)
    readonly header!: QueryList<MenuHeader>;

    // Quando preenchidos, determina que este menu, na verdade, é um submenu.
    // Ele inicia escondido e sua exibição é controlada pelo menu pai.
    protected readonly parentMenu = input<Menu | null>(null);
    protected readonly parentItem = input<MenuItem | null>(null);

    @ContentChildren(MenuOption)
    readonly menuOptions!: QueryList<MenuOption>;

    private isSubMenu = false;
    private isVisible = false;

    constructor(
        private menuManager: MenuManager,
        public elementRef: ElementRef,
        private router: Router,
        @Optional() @Host() private parentButton: Button,
    ) {
        if (parentButton) {
            this.parentButton.setMenu(this);
        }
    }

    public ngOnInit(): void {
        this.menuManager.register(this);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['parentItem'] && changes['parentItem'].currentValue) {
            this.isSubMenu = true;
            this.parentItem()!.childMenu = this;
        }
    }

    public getBoundingClientRect(): DOMRect {
        return this.elementRef.nativeElement.getBoundingClientRect();
    }

    public toggleVisibility(event: MouseEvent | null = null): void {
        if (this.isVisible) {
            this.menuOptions.forEach((option) => {
                option.childMenu?.hide();
            });
            this.hide();
        } else {
            this.show(event);
        }
    }

    public show(_event: MouseEvent | null = null): void {
        this.isVisible = true;

        if (this.parentMenu()) {
            const box = this.parentMenu()!.getBoundingClientRect();

            // Ancora o submenu ao ITEM-pai (o `<li>` que o contém no DOM), não à
            // posição do mouse — assim ele aparece sempre ao lado da opção
            // correta, e não em alturas variáveis conforme o cursor.
            const anchor = (
                this.elementRef.nativeElement as HTMLElement
            ).closest('li');
            const top = (anchor?.getBoundingClientRect().top ?? box.top) - 4;

            let width = 200;

            try {
                width = this.parentMenu()!.style().width.toInt();
            } catch (e) {
                width = 200;
            }

            const left = this.clampSubmenuLeft(box, width);

            this.style.update((prevStyle) => ({
                ...prevStyle,
                visibility: 'visible',
                top: top + 'px',
                left: left + 'px',
            }));
        } else {
            this.style.update((prevStyle) => ({
                ...prevStyle,
                visibility: 'visible',
            }));
        }
    }

    /**
     * Calcula a coordenada horizontal (`left`) do submenu mantendo-o dentro da
     * viewport. Por padrão o submenu abre à direita do menu-pai
     * (`box.left + parentWidth`); se não couber (telas estreitas), abre à esquerda
     * do pai. Como último recurso, ancora a 8px da borda esquerda.
     *
     * @param parentBox Retângulo do menu-pai (`getBoundingClientRect`).
     * @param parentWidth Largura do menu-pai, em px.
     * @returns Coordenada `left` em px, já limitada à viewport.
     */
    private clampSubmenuLeft(parentBox: DOMRect, parentWidth: number): number {
        const margin = 8;
        const viewportWidth =
            typeof window !== 'undefined' ? window.innerWidth : Infinity;

        let submenuWidth = 200;
        try {
            submenuWidth = this.style().width.toInt();
        } catch (e) {
            submenuWidth = 200;
        }

        const rightLeft = parentBox.left + parentWidth;
        if (rightLeft + submenuWidth <= viewportWidth - margin) {
            return rightLeft;
        }

        // Não cabe à direita: tenta à esquerda do menu-pai.
        const leftLeft = parentBox.left - submenuWidth;
        if (leftLeft >= margin) {
            return leftLeft;
        }

        // Nem à esquerda: ancora à borda, respeitando a margem.
        return Math.max(margin, viewportWidth - submenuWidth - margin);
    }

    public hide(): void {
        this.isVisible = false;
        this.style.update((prevStyle) => ({
            ...prevStyle,
            visibility: 'hidden',
        }));
    }

    protected handleSubMenuClick(item: MenuItem): void {
        this.itemClick.emit(item);
    }

    /** Timer pendente para fechar o submenu ao sair do item (cancelável). */
    private submenuHideTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Abre o submenu do item ao passar o mouse por cima (hover), fechando os
     * submenus de outros itens. Cancela um fechamento pendente (permite mover o
     * cursor do item até o submenu sem que ele feche).
     */
    protected onItemMouseEnter(item: MenuItem, event: MouseEvent): void {
        if (this.submenuHideTimer) {
            clearTimeout(this.submenuHideTimer);
            this.submenuHideTimer = null;
        }

        this.hideOtherSubmenus(item);

        if (item.children && item.children.isNotEmpty()) {
            item.childMenu?.show(event);
        }
    }

    /**
     * Agenda o fechamento do submenu ao sair do item. O atraso permite que o
     * cursor atravesse até o submenu (que é filho do item no DOM) sem fechá-lo.
     */
    protected onItemMouseLeave(item: MenuItem): void {
        if (item.children && item.children.isNotEmpty()) {
            this.submenuHideTimer = setTimeout(
                () => item.childMenu?.hide(),
                200,
            );
        }
    }

    /** Fecha os submenus de todos os itens, exceto o informado. */
    private hideOtherSubmenus(keep: MenuItem): void {
        (this.items() ?? []).forEach((other) => {
            if (other !== keep) {
                other.childMenu?.hide();
            }
        });
        this.menuOptions?.forEach((option) => {
            const other = option.getMenuItem();
            if (other !== keep) {
                other.childMenu?.hide();
            }
        });
    }

    protected async handleClickMenuItem(
        event: Event,
        item: MenuItem,
    ): Promise<void> {
        // Impede que o clique borbulhe para elementos que contêm o menu (ex.:
        // uma linha clicável), disparando ações indesejadas ao escolher a opção.
        event.stopPropagation();

        if (item.disabled) {
            return;
        }
        if (item.children && item.children.isNotEmpty()) {
            // O hover já controla a abertura; no clique apenas garante aberto
            // (não alterna, para não fechar o que o hover abriu).
            item.childMenu?.show(event as MouseEvent);
        } else {
            if (this.parentButton) {
                this.parentButton.closeMenu();
            }

            if (item.routerLink) {
                await this.router.navigate(item.routerLink);
            } else {
                this.itemClick.emit(item);
            }
        }
    }
}
